import { isAxiosError } from 'axios';
import { useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import Typeahead from 'components/inputs/typeahead';
import { usePhoneTextability } from 'hooks/use-phone-textability';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { extractApiErrorMessages } from 'utils/extract-api-error-messages';
import { buildPhoneOptions } from 'utils/phone-options';
import { errorModal, successModal } from 'utils/ui';

type SendZoomUrlModalProps = {
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
};

export default function SendZoomUrlModal({ openState }: SendZoomUrlModalProps) {
  const { projectDetails } = useAppSelector((state) => state.project);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = openState;

  useEffect(() => {
    setPhoneNumber('');
    setError(null);
  }, [open]);

  // The project's customer numbers are offered like the photo-collector
  // modal does, each marked with its line type; numbers that cannot be
  // texted (landline, opted out) are listed in red and cannot be chosen.
  const knownNumbers = useMemo(
    () => projectDetails?.customerPhoneNumbers ?? [],
    [projectDetails?.customerPhoneNumbers]
  );
  const phonesToCheck = useMemo(
    () => [...knownNumbers, phoneNumber],
    [knownNumbers, phoneNumber]
  );
  const { byKey, textabilityOf } = usePhoneTextability({
    phones: phonesToCheck,
    enabled: open
  });
  const options = useMemo(
    () => buildPhoneOptions(knownNumbers, byKey),
    [knownNumbers, byKey]
  );
  const blockedReason = textabilityOf(phoneNumber)?.reason ?? null;

  const handleSendText = async () => {
    if (blockedReason) {
      setError(blockedReason);
      return;
    }
    if (/^\+?1?\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_PARTNERNET_URL}/api/pnet/system-selector/send-zoom-url`,
          { phoneNumber }
        );

        if (response.status >= 200 && response.status < 300) {
          setOpen(false);
          successModal('Text message sent');
        } else {
          setError('Failed to send text message');
          errorModal('Failed to send text message');
        }
      } catch (err) {
        // pnet-api refuses texts to landlines and opted-out customers with a
        // 400 that says why (same gate as every other text path) — show that
        // reason instead of a generic failure.
        const [refusal] =
          isAxiosError(err) && err.response?.status === 400
            ? extractApiErrorMessages(err)
            : [];
        if (refusal) {
          setError(refusal);
          errorModal(refusal);
        } else {
          setError('An unexpected error occurred, please try again');
          errorModal('Something went wrong');
        }
      }
    } else {
      setError('Please enter a valid phone number');
    }
  };

  return (
    <Modal
      title='Enter a Phone Number'
      buttonText='Send Text'
      openState={openState}
      onSave={handleSendText}
      saveDisabled={!!blockedReason}
      closeOnSave={false}
    >
      <div className='flex flex-col gap-2'>
        <Typeahead
          placeholder='(123) 456-7890'
          options={options}
          onChange={(value) => {
            setPhoneNumber(value.toString());
            setError(null);
          }}
          customOptionFormat={(input) => `New Number: ${input}`}
          allowCustom
        />
        {(error || blockedReason) && (
          <div className='text-sm text-red-500'>{error || blockedReason}</div>
        )}
      </div>
    </Modal>
  );
}
